import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import * as providerAdminApi from '../../../api/providerAdminApi';
import { useAuth } from '../../../auth/AuthProvider';
import { canAccessProviderAdmin } from './providerAdminAccess';

const ORIGINATOR_QUERY = {
  page: 1,
  page_size: 100,
  org_type: 'originator',
  status: 'ACTIVE',
};

const CUSTODIAN_QUERY = {
  page: 1,
  page_size: 100,
  org_type: 'custodian',
  status: 'ACTIVE',
};

function isForbidden(error) {
  return error?.status === 403;
}

function orgItems(page) {
  const items = Array.isArray(page?.items) ? page.items : [];
  return items
    .filter((org) => org && org.id)
    .sort((left, right) => String(left.name || '').localeCompare(String(right.name || '')));
}

function organizationLabel(org) {
  if (org.name && org.code && org.name !== org.code) {
    return `${org.name} (${org.code})`;
  }
  return org.name || org.code || org.id;
}

export function ProgramCreatePage() {
  const { session } = useAuth();
  const navigate = useNavigate();
  const allowed = canAccessProviderAdmin(session);
  const [originators, setOriginators] = useState([]);
  const [custodians, setCustodians] = useState([]);
  const [form, setForm] = useState({
    originator_organization_id: '',
    name: '',
    code: '',
    description: '',
    custodian_organization_id: '',
  });
  const [error, setError] = useState(null);
  const [forbidden, setForbidden] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!allowed) {
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.all([
      providerAdminApi.listOrganizations(ORIGINATOR_QUERY),
      providerAdminApi.listOrganizations(CUSTODIAN_QUERY),
    ])
      .then(([originatorPage, custodianPage]) => {
        if (cancelled) return;
        setOriginators(orgItems(originatorPage));
        setCustodians(orgItems(custodianPage));
      })
      .catch((err) => {
        if (cancelled) return;
        setOriginators([]);
        setCustodians([]);
        if (isForbidden(err)) {
          setForbidden(true);
          return;
        }
        setError(err.message || 'Failed to load organizations');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [allowed]);

  if (!allowed || forbidden) {
    return (
      <p className="text-sm text-eorig-muted">
        Provider Admin access is required to create programs.
      </p>
    );
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    const payload = {
      originator_organization_id: form.originator_organization_id,
      name: form.name.trim(),
      code: form.code.trim(),
      description: form.description.trim() || null,
      custodian_organization_id: form.custodian_organization_id || null,
    };
    if (!payload.originator_organization_id || !payload.name || !payload.code) {
      setError('Originator, name, and code are required.');
      setSaving(false);
      return;
    }
    try {
      const created = await providerAdminApi.createProviderProgram(payload);
      if (!created?.id) {
        setError('Create failed');
        return;
      }
      navigate(`/provider/programs-rules?program=${created.id}`);
    } catch (err) {
      setError(err.message || 'Create failed');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <p className="text-sm text-eorig-muted">Loading organizations…</p>;
  }

  return (
    <form onSubmit={handleSubmit} className="max-w-xl space-y-4 rounded-2xl border border-eorig-border bg-white p-5">
      <p className="text-sm text-eorig-muted">
        The program is saved on the originator you select. Admission rules are configured after it is saved.
      </p>
      {error ? (
        <p className="text-sm text-red-600" role="alert">
          {error}
        </p>
      ) : null}
      <label className="block text-sm">
        <span className="mb-1 block font-medium">Originator</span>
        <select
          required
          value={form.originator_organization_id}
          onChange={(e) => setForm((current) => ({ ...current, originator_organization_id: e.target.value }))}
          className="w-full rounded-xl border border-eorig-border px-3 py-2"
        >
          <option value="">Select an originator</option>
          {originators.map((org) => (
            <option key={org.id} value={org.id}>
              {organizationLabel(org)}
            </option>
          ))}
        </select>
      </label>
      <label className="block text-sm">
        <span className="mb-1 block font-medium">Name</span>
        <input
          required
          value={form.name}
          onChange={(e) => setForm((current) => ({ ...current, name: e.target.value }))}
          className="w-full rounded-xl border border-eorig-border px-3 py-2"
        />
      </label>
      <label className="block text-sm">
        <span className="mb-1 block font-medium">Code</span>
        <input
          required
          value={form.code}
          onChange={(e) => setForm((current) => ({ ...current, code: e.target.value }))}
          className="w-full rounded-xl border border-eorig-border px-3 py-2"
        />
      </label>
      <label className="block text-sm">
        <span className="mb-1 block font-medium">Description</span>
        <input
          value={form.description}
          placeholder="Optional"
          onChange={(e) => setForm((current) => ({ ...current, description: e.target.value }))}
          className="w-full rounded-xl border border-eorig-border px-3 py-2"
        />
      </label>
      <label className="block text-sm">
        <span className="mb-1 block font-medium">Custodian</span>
        <select
          value={form.custodian_organization_id}
          onChange={(e) => setForm((current) => ({ ...current, custodian_organization_id: e.target.value }))}
          className="w-full rounded-xl border border-eorig-border px-3 py-2"
        >
          <option value="">None</option>
          {custodians.map((org) => (
            <option key={org.id} value={org.id}>
              {organizationLabel(org)}
            </option>
          ))}
        </select>
      </label>
      <div className="flex gap-3">
        <button
          type="submit"
          disabled={saving}
          className="rounded-xl bg-[#003A70] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
        >
          {saving ? 'Creating…' : 'Create program'}
        </button>
        <Link to="/provider/admin/programs" className="rounded-xl px-4 py-2 text-sm text-eorig-muted">
          Cancel
        </Link>
      </div>
    </form>
  );
}
